/**
 * Share StrictMode duplicate loads for one revision while serializing a follow-up requested for a
 * newer revision. A result superseded while in flight is discarded rather than briefly replacing
 * the store with a representation the caller already knows is stale.
 */
export function createRevisionLoader<T>(load: () => Promise<T>, apply: (value: T) => void) {
  let loading: { revision: number; promise: Promise<void> } | null = null;
  let latestRequestedRevision: number | undefined;

  const loadRevision = (revision: number): Promise<void> => {
    latestRequestedRevision = revision;
    if (loading) {
      if (loading.revision === revision) return loading.promise;
      return loading.promise.catch(() => undefined).then(() => loadRevision(revision));
    }

    const pending = load()
      .then((value) => {
        if (latestRequestedRevision === revision) apply(value);
      })
      .finally(() => {
        if (loading?.promise === pending) loading = null;
      });
    loading = { revision, promise: pending };
    return pending;
  };

  return loadRevision;
}
